import { formatRupees, type PlanKind } from '@quickreport/domain';
import {
  BookOpen,
  CloudOff,
  Lock,
  Mail,
  Phone,
  QrCode,
  Server,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { ExternalLink } from '../../components/ExternalLink';
import { api } from '../../lib/api';
import { desktop } from '../../lib/desktop';
import { useNotifications } from '../../lib/notifications';

interface Plan {
  id: string;
  kind: PlanKind;
  validityDays: number | null;
  priceOnce: number;
  perReport: number;
  name: string;
  description: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  maxUsers: number | null;
  highlight: boolean;
}
interface About {
  guideUrl: string;
  website: string;
  supportEmail: string;
  supportPhone: string;
  plans: Plan[];
}

const FALLBACK: About = {
  guideUrl: 'https://aharbal.com/labview/guide',
  website: 'https://aharbal.com',
  supportEmail: '',
  supportPhone: '',
  plans: [],
};
const CACHE = 'qr.about';

/** The About details: fresh from the server when online, the last copy when not. */
function useAbout(): About {
  const [about, setAbout] = useState<About>(() => {
    try {
      const raw = localStorage.getItem(CACHE);
      return raw ? { ...FALLBACK, ...(JSON.parse(raw) as About) } : FALLBACK;
    } catch {
      return FALLBACK;
    }
  });
  useEffect(() => {
    let alive = true;
    api<About>('GET', '/v1/about')
      .then((a) => {
        if (!alive) return;
        setAbout(a);
        try {
          localStorage.setItem(CACHE, JSON.stringify(a));
        } catch {
          // storage blocked: the copy is simply not kept
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return about;
}

const POINTS = [
  {
    icon: CloudOff,
    title: 'Works without internet',
    text: 'Every computer keeps your lab’s data and syncs when it is back online.',
  },
  {
    icon: QrCode,
    title: 'Reports patients can verify',
    text: 'A QR code on each signed report, and a code sent to the patient’s own phone.',
  },
  {
    icon: Wallet,
    title: 'Prices stay private',
    text: 'Prices, payments and expenses are for your lab only. They never appear on a report.',
  },
  {
    icon: ShieldCheck,
    title: 'Every change is recorded',
    text: 'Who did what and when, with corrections kept as new versions.',
  },
];

export function AboutPage() {
  const about = useAbout();
  const d = desktop();
  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-8">
      <header className="flex items-center gap-4">
        <img src="/labview-mark.svg" alt="" className="h-16 w-16" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">LabView</h1>
          <p className="text-slate">
            by{' '}
            <ExternalLink href={about.website} className="text-brand hover:underline">
              Aharbal
            </ExternalLink>
            {' · '}
            {d ? `Desktop app, version ${d.version}` : 'Web version'}
          </p>
        </div>
      </header>

      <p className="mt-6 max-w-2xl text-[15px] leading-relaxed">
        LabView is report software for medical laboratories. You register a patient, enter results,
        sign the report and give it to the patient, with the lab’s own letterhead, on the desktop or
        in the browser. It keeps working when the internet does not.
      </p>

      <section aria-label="Highlights" className="mt-6 grid gap-3 sm:grid-cols-2">
        {POINTS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex gap-3 rounded-xl border border-line bg-paper p-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent">
              <Icon size={18} />
            </span>
            <div>
              <div className="font-semibold">{title}</div>
              <div className="text-[13px] text-slate">{text}</div>
            </div>
          </div>
        ))}
      </section>

      <section
        className="mt-8 flex flex-wrap items-center gap-4 rounded-xl border border-accent/25 bg-accent-soft p-5"
        aria-label="User guide"
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-paper text-accent">
          <BookOpen size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">New to LabView? Watch the guide</h2>
          <p className="text-[13px] text-slate">
            A video tour of the desktop app, the patient’s phone and the web version, with every
            step shown on screen.
          </p>
        </div>
        <ExternalLink
          href={about.guideUrl}
          className="inline-flex h-9 items-center rounded-lg bg-navy px-4 font-medium text-white shadow-sm hover:bg-navy-hover"
        >
          Open the guide
        </ExternalLink>
      </section>
      <p className="mt-2 text-[12px] break-all text-slate">{about.guideUrl}</p>

      <p className="mt-6 text-[13px] text-slate">
        <ExternalLink
          href="https://labview.aharbal.com/privacy.html"
          className="text-brand hover:underline"
        >
          Privacy policy
        </ExternalLink>
      </p>

      <YourPlan />
      {about.plans.length ? (
        <section id="plans" className="mt-10 scroll-mt-4" aria-label="Plans and pricing">
          <h2 className="text-lg font-semibold">Plans and pricing</h2>
          <p className="mt-1 text-[13px] text-slate">
            Prices are in rupees. Ask the LabView team to start or change your plan.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {about.plans.map((p) => (
              <div
                key={p.id}
                className={`flex flex-col rounded-xl border bg-paper p-5 ${p.highlight ? 'border-accent ring-1 ring-accent/30' : 'border-line'}`}
              >
                {p.highlight ? (
                  <span className="mb-2 w-fit rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-accent">
                    Most popular
                  </span>
                ) : null}
                <h3 className="text-base font-semibold">{p.name}</h3>
                {p.description ? (
                  <p className="mt-0.5 text-[13px] text-slate">{p.description}</p>
                ) : null}
                <PlanPrice p={p} />
                <ul className="mt-3 grid gap-1.5 text-[13px]">
                  {p.maxUsers ? <li>Up to {p.maxUsers} people</li> : null}
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span aria-hidden className="text-accent">
                        ✓
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-10 grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-line bg-paper p-5" aria-label="Your data">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Lock size={16} /> Your data
          </h2>
          <ul className="mt-2 grid gap-2 text-[13px] text-slate">
            <li>Your lab’s records live on your computers and in your lab’s account.</li>
            <li>
              Prices, payments and expenses are never printed on a report or shown to a patient.
            </li>
            <li>
              To recover from a fault or an accidental deletion, the LabView team keeps encrypted
              backups of every lab. They cannot be read without a key the team holds.
            </li>
            <li>
              The LabView team sees how many reports and users a lab has, not what is in them.
            </li>
          </ul>
        </section>
        <section className="rounded-xl border border-line bg-paper p-5" aria-label="Help">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Server size={16} /> Help and contact
          </h2>
          <ul className="mt-2 grid gap-2 text-[13px]">
            {about.supportEmail ? (
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-slate" />
                <span className="select-all">{about.supportEmail}</span>
              </li>
            ) : null}
            {about.supportPhone ? (
              <li className="flex items-center gap-2">
                <Phone size={14} className="text-slate" />
                <span className="select-all">{about.supportPhone}</span>
              </li>
            ) : null}
            <li>
              <ExternalLink href={about.website} className="text-brand hover:underline">
                {about.website.replace(/^https?:\/\//, '')}
              </ExternalLink>
            </li>
          </ul>
          {!about.supportEmail && !about.supportPhone ? (
            <p className="mt-2 text-[13px] text-slate">
              Ask the person who set LabView up for your lab, or open the guide.
            </p>
          ) : null}
        </section>
      </section>
    </div>
  );
}

const lengthOf = (days: number) =>
  days === 365
    ? '1 year'
    : days % 30 === 0
      ? `${days / 30} month${days === 30 ? '' : 's'}`
      : `${days} days`;

function PlanPrice({ p }: { p: Plan }) {
  if (p.kind === 'validity') {
    return (
      <div className="mt-3">
        <span className="text-2xl font-semibold">
          {p.priceOnce > 0 ? formatRupees(p.priceOnce * 100) : 'Free'}
        </span>
        <div className="text-[12px] text-slate">
          for {p.validityDays ? lengthOf(p.validityDays) : 'a fixed time'}
        </div>
      </div>
    );
  }
  if (p.kind === 'per_report') {
    return (
      <div className="mt-3">
        <span className="text-2xl font-semibold">
          {formatRupees(Math.round(p.perReport * 100))}
        </span>
        <span className="text-[13px] text-slate"> / report</span>
        <div className="text-[12px] text-slate">Pay only for the reports you make</div>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <span className="text-2xl font-semibold">{formatRupees(p.priceMonthly * 100)}</span>
      <span className="text-[13px] text-slate"> / month</span>
      {p.priceYearly > 0 ? (
        <div className="text-[12px] text-slate">or {formatRupees(p.priceYearly * 100)} a year</div>
      ) : null}
    </div>
  );
}

interface LedgerLine {
  id: string;
  at: string;
  kind: string;
  amountPaise: number;
  balanceAfterPaise: number;
  note: string;
}

/** This lab's own plan: what it is, how long or how much is left, and wallet history. */
function YourPlan() {
  const n = useNotifications();
  const [ledger, setLedger] = useState<LedgerLine[]>([]);
  useEffect(() => {
    let alive = true;
    api<{ ledger: LedgerLine[] }>('GET', '/v1/plan')
      .then((r) => alive && setLedger(r.ledger))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  const { plan, planStatus: st, planNotice: notice } = n;
  if (!plan) return null;
  const date = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';
  const tone =
    st.state === 'expired' || st.state === 'empty'
      ? 'border-flag/40 bg-red-50'
      : st.needsAttention
        ? 'border-warn/40 bg-amber-50'
        : 'border-line bg-paper';
  return (
    <section className={`mt-8 rounded-xl border p-5 ${tone}`} aria-label="Your plan">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">Your plan</h2>
        <span className="rounded-full bg-paper px-2 py-0.5 text-[12px] font-medium ring-1 ring-line">
          {plan.name}
        </span>
      </div>
      {plan.kind === 'per_report' ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Figure
            label="Wallet"
            value={formatRupees(plan.wallet.balancePaise)}
            bad={plan.wallet.balancePaise < 0}
          />
          <Figure label="Price per report" value={formatRupees(plan.wallet.reportPricePaise)} />
          <Figure
            label="Reports you can still make"
            value={st.reportsLeft === null ? 'No limit' : String(st.reportsLeft)}
            bad={!st.canCreateReports}
          />
        </div>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Figure label="Started" value={date(plan.startedAt) || '—'} />
          <Figure
            label={st.state === 'expired' ? 'Ended' : 'Valid until'}
            value={date(plan.expiresAt) || 'No end date'}
            bad={st.state === 'expired'}
          />
          <Figure
            label="Time left"
            value={
              st.daysLeft === null
                ? '—'
                : st.daysLeft < 0
                  ? 'Ended'
                  : `${st.daysLeft} day${st.daysLeft === 1 ? '' : 's'}`
            }
            bad={st.state === 'expired'}
          />
        </div>
      )}
      {notice ? (
        <p className="mt-3 text-[13px]">
          <b>{notice.title}.</b> {notice.body} To start or change a plan, contact LabView using the
          details below.
        </p>
      ) : null}
      {ledger.length > 0 ? (
        <details className="mt-3 text-[13px]">
          <summary className="cursor-pointer font-medium">Wallet history</summary>
          <ul className="mt-2 divide-y divide-line rounded-lg border border-line bg-paper">
            {ledger.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 px-3 py-1.5">
                <span className="w-24 text-slate">
                  {new Date(e.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
                <span className="min-w-0 flex-1">
                  {e.kind === 'report'
                    ? `Report ${e.note}`
                    : e.kind === 'topup'
                      ? `Credit added${e.note ? `: ${e.note}` : ''}`
                      : `Correction: ${e.note}`}
                </span>
                <span className={`font-medium ${e.amountPaise < 0 ? 'text-flag' : 'text-ok'}`}>
                  {e.amountPaise < 0 ? '−' : '+'}
                  {formatRupees(Math.abs(e.amountPaise))}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

function Figure({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="rounded-lg bg-paper/80 p-3 ring-1 ring-line">
      <div className="text-[11px] tracking-wide text-slate uppercase">{label}</div>
      <div className={`text-lg font-semibold ${bad ? 'text-flag' : ''}`}>{value}</div>
    </div>
  );
}
